import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
} from "@/components/ui/carousel";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { TrendingUp, TrendingDown, Minus } from "lucide-react";
import { useState, useCallback, useEffect } from "react";
import { cn } from "@/lib/utils";

// Helper to check if a column should be formatted as percentage
function isPercentageColumn(key) {
  const lowerKey = key.toLowerCase();
  return (
    lowerKey.includes("percent") ||
    lowerKey.includes("pct") ||
    lowerKey.includes("rate") ||
    lowerKey.includes("ratio") ||
    lowerKey.endsWith("_share")
  );
}

// Helper to format numbers nicely
function formatNumber(num, columnKey = "") {
  if (typeof num !== "number") return num;

  const isGrowth = isGrowthMetric(columnKey);
  const isPercent = isPercentageColumn(columnKey);

  let formatted;

  // If it's a percentage column, always format as percentage
  if (isPercent) {
    formatted = `${(num * 100).toFixed(1)}%`;
  } else if (Math.abs(num) >= 1000000000) {
    // Billions
    formatted = `${(num / 1000000000).toFixed(1)}B`;
  } else if (Math.abs(num) >= 1000000) {
    // Millions
    formatted = `${(num / 1000000).toFixed(1)}M`;
  } else if (Math.abs(num) >= 1000) {
    // Thousands
    formatted = `${(num / 1000).toFixed(1)}K`;
  } else if (Math.abs(num) < 1 && Math.abs(num) > 0) {
    // Small decimals that aren't percentages - check if they might be percentages
    formatted = `${(num * 100).toFixed(1)}%`;
  } else {
    formatted = num.toFixed(0);
  }

  // Clean up .0 suffixes for cleaner display
  formatted = formatted.replace(/\.0([KMB%]|$)/, "$1");

  // Add + prefix for positive growth metrics
  if (isGrowth && num > 0) {
    formatted = "+" + formatted;
  }

  return formatted;
}

// Helper to check if a column is a growth metric
function isGrowthMetric(key) {
  const lowerKey = key.toLowerCase();
  return (
    lowerKey.includes("yoy") ||
    lowerKey.includes("mom") ||
    lowerKey.includes("qoq") ||
    lowerKey.includes("wow") ||
    lowerKey.includes("percent_increase") ||
    lowerKey.includes("raw_increase") ||
    lowerKey.includes("increase")
  );
}

// Helper to format percentage
function formatPercent(num) {
  if (typeof num !== "number") return num;
  return `${(num * 100).toFixed(2)}%`;
}

// Component for single metric display
function MetricCard({ label, value, trend }) {
  // For growth metrics, the value IS the trend, so don't show both
  const isGrowth = isGrowthMetric(label);
  const displayTrend = trend !== undefined && !isGrowth;

  const getTrendIcon = () => {
    const checkValue = isGrowth ? value : trend;
    if (checkValue === null || checkValue === undefined) return null;
    if (checkValue > 0)
      return <TrendingUp className="h-4 w-4 text-green-600 dark:text-green-400 shrink-0" />;
    if (checkValue < 0) return <TrendingDown className="h-4 w-4 text-red-600 dark:text-red-400 shrink-0" />;
    return <Minus className="h-4 w-4 text-muted-foreground shrink-0" />;
  };

  return (
    <div className="space-y-1">
      <p className="text-sm text-muted-foreground">{label}</p>
      <div className="flex items-center gap-2">
        <p
          className={`text-2xl font-bold ${isGrowth ? (value < 0 ? "text-red-600 dark:text-red-400" : value > 0 ? "text-green-600 dark:text-green-400" : "") : ""}`}
        >
          {formatNumber(value, label)}
        </p>
        {getTrendIcon()}
      </div>
      {displayTrend && (
        <p
          className={`text-xs ${trend >= 0 ? "text-green-600 dark:text-green-400" : "text-red-600 dark:text-red-400"}`}
        >
          {trend >= 0 ? "+" : ""}
          {formatPercent(trend)}
        </p>
      )}
    </div>
  );
}

const COLORS_BY_DATASOURCE = {
  google: "#4285F4",
  tiktok: "#FF0050",
  instagram: "#FF6B35",
};

function getDataSourceColor(dataSource) {
  return COLORS_BY_DATASOURCE[dataSource?.toLowerCase()] || "hsl(var(--primary))";
}

const DISPLAY_NAMES_BY_DATASOURCE = {
  google: "Google",
  tiktok: "TikTok",
  instagram: "Instagram",
  popindex: "Popularity Index",
};

function getDataSourceDisplayName(dataSource) {
  return DISPLAY_NAMES_BY_DATASOURCE[dataSource?.toLowerCase()] || dataSource;
}

function TimeSeriesChart({ data, title, dataSource }) {
  const chartData = data.map((item) => ({
    date: new Date(item.date).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    }),
    value: item.real_volume || item.volume,
    volume: item.volume,
  }));
  const chartColor = getDataSourceColor(dataSource);

  return (
    <ResponsiveContainer width="100%" height={300}>
      <AreaChart data={chartData}>
        <defs>
          <linearGradient
            id={`colorGradient-${dataSource || "default"}`}
            x1="0"
            y1="0"
            x2="0"
            y2="1"
          >
            <stop offset="5%" stopColor={chartColor} stopOpacity={0.3} />
            <stop offset="95%" stopColor={chartColor} stopOpacity={0.1} />
          </linearGradient>
        </defs>
        <CartesianGrid strokeDasharray="3 3" className="stroke-border opacity-50" />
        <XAxis dataKey="date" className="text-xs" tick={{ fill: "currentColor" }} />
        <YAxis
          className="text-xs"
          tick={{ fill: "currentColor" }}
          tickFormatter={(val) => formatNumber(val)}
        />
        <Tooltip
          contentStyle={{
            backgroundColor: "hsl(var(--background))",
            border: "1px solid hsl(var(--border))",
            borderRadius: "6px",
          }}
          labelStyle={{ color: "hsl(var(--foreground))" }}
          formatter={(value) => formatNumber(value)}
        />
        <Area
          type="monotone"
          dataKey="value"
          stroke={chartColor}
          fill={`url(#colorGradient-${dataSource || "default"})`}
          strokeWidth={2}
        />
      </AreaChart>
    </ResponsiveContainer>
  );
}

function CarouselDots({ count, current, onSelect }) {
  return (
    <div className="flex justify-center gap-2 mt-4">
      {Array.from({ length: count }).map((_, index) => (
        <button
          key={index}
          onClick={() => onSelect(index)}
          className={cn(
            "w-2 h-2 rounded-full transition-all",
            index === current
              ? "bg-primary w-4"
              : "bg-muted-foreground/30 hover:bg-muted-foreground/50"
          )}
          aria-label={`Go to slide ${index + 1}`}
        />
      ))}
    </div>
  );
}

function TimeSeriesCarousel({ items }) {
  const [api, setApi] = useState(null);
  const [current, setCurrent] = useState(0);

  useEffect(() => {
    if (!api) return;

    const onSelect = () => {
      setCurrent(api.selectedScrollSnap());
    };

    api.on("select", onSelect);
    return () => {
      api.off("select", onSelect);
    };
  }, [api]);

  const scrollTo = useCallback(
    (index) => {
      api?.scrollTo(index);
    },
    [api]
  );

  return (
    <div>
      <Carousel setApi={setApi} opts={{ loop: false }}>
        <CarouselContent>
          {items.map((item, index) => (
            <CarouselItem key={index}>
              <div className="space-y-4">
                {item.path && (
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="text-xs text-muted-foreground">
                        {formatPath(item.path).split(" › ").slice(0, -1).join(" › ")}
                      </h4>
                      <h3 className="text-lg font-semibold">
                        {formatPath(item.path).split(" › ").pop()}
                      </h3>
                    </div>
                    {item.data_source && (
                      <Badge variant="secondary" className="text-xs">
                        {getDataSourceDisplayName(item.data_source)}
                      </Badge>
                    )}
                  </div>
                )}
                {item.data && (
                  <div className="flex flex-wrap gap-16 mb-8">
                    {item.data.non_cum_YoY_rolling !== undefined && (
                      <MetricCard
                        label="YoY"
                        value={item.data.non_cum_YoY_rolling}
                        trend={item.data.non_cum_YoY_rolling}
                      />
                    )}
                    {item.data.non_cum_QoQ_rolling !== undefined && (
                      <MetricCard
                        label="QoQ"
                        value={item.data.non_cum_QoQ_rolling}
                        trend={item.data.non_cum_QoQ_rolling}
                      />
                    )}
                    {item.data.non_cum_MoM_rolling !== undefined && (
                      <MetricCard
                        label="MoM"
                        value={item.data.non_cum_MoM_rolling}
                        trend={item.data.non_cum_MoM_rolling}
                      />
                    )}
                    {item.data.non_cum_avg_weekly_posts !== undefined && (
                      <MetricCard label="Avg. Weekly Posts" value={item.data.non_cum_avg_weekly_posts} />
                    )}
                  </div>
                )}

                {item.time_series && (
                  <TimeSeriesChart
                    data={item.time_series}
                    title={formatPath(item.path) || "Time Series"}
                    dataSource={item.data_source}
                  />
                )}
              </div>
            </CarouselItem>
          ))}
        </CarouselContent>
      </Carousel>
      {items.length > 1 && <CarouselDots count={items.length} current={current} onSelect={scrollTo} />}
    </div>
  );
}

function extractLeafFromPath(pathStr) {
  if (!pathStr || typeof pathStr !== "string") return pathStr;
  const parts = pathStr.split(">").map((s) => s.trim());
  return parts[parts.length - 1] || pathStr;
}

function formatPath(pathStr) {
  if (!pathStr || typeof pathStr !== "string") return pathStr;
  let parts = pathStr.split(">").map((s) => s.trim());
  if (parts[0]?.toLowerCase() === "category") {
    parts = parts.slice(1);
  }
  parts = parts.map((part) => {
    if (!part) return part;
    return part.charAt(0).toUpperCase() + part.slice(1);
  });
  return parts.join(" › ");
}

const COLUMN_LABELS_BY_KEY = {
  yoy_percent_increase: "YoY",
  yoy_raw_increase: "Increase YoY",
  trend_path: "Category",
};

function formatColumnHeader(key) {
  if (COLUMN_LABELS_BY_KEY[key]) {
    return COLUMN_LABELS_BY_KEY[key];
  }
  if (key.includes("_path")) {
    return key
      .replace(/cat\d+_path/, "Category")
      .replace(/brand_path/, "Brand")
      .replace(/_/g, " ")
      .replace(/\b\w/g, (l) => l.toUpperCase());
  }
  return key.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());
}

function DataTable({ data, title, dataSources, regions }) {
  if (!data || data.length === 0) return null;

  // Get all unique keys from the data
  const keys = [...new Set(data.flatMap((item) => Object.keys(item)))];

  // Filter out complex objects, UUID columns, item_path, trend_id, and path
  const simpleKeys = keys.filter((key) => {
    // Skip UUID columns (cat*_id, brand_id), item_path, trend_id, and path
    if (
      key.match(/^(cat\d+|brand)_id$/) ||
      key === "item_id" ||
      key === "item_path" ||
      key === "trend_id" ||
      key === "path"
    ) {
      return false;
    }

    const sampleValue = data.find((item) => item[key] !== undefined)?.[key];
    return typeof sampleValue !== "object" || sampleValue === null;
  });

  // Determine the sorting column (look for YoY, increase, or other metric columns)
  const sortingColumns = [
    "yoy_raw_increase",
    "yoy_percent_increase",
    "percent_increase",
    "raw_increase",
    "volume",
    "count",
  ];
  let sortColumn = sortingColumns.find((col) => simpleKeys.includes(col));

  // If no standard sorting column found, find the first numeric column
  if (!sortColumn) {
    sortColumn = simpleKeys.find((key) => {
      const sampleValue = data[0]?.[key];
      return typeof sampleValue === "number";
    });
  }

  // Sort data by the determined column (descending)
  const sortedData = sortColumn
    ? [...data].sort((a, b) => {
        const aVal = a[sortColumn] || 0;
        const bVal = b[sortColumn] || 0;
        return bVal - aVal; // Descending order
      })
    : data;

  // Identify name/category columns (should appear first)
  const nameColumns = simpleKeys.filter((key) => {
    const lowerKey = key.toLowerCase();
    return (
      lowerKey === "trend" ||
      lowerKey === "name" ||
      lowerKey === "hashtag" ||
      lowerKey.includes("_path") ||
      lowerKey.includes("category") ||
      lowerKey.match(/^cat\d+_path$/)
    );
  });

  // Identify numeric columns
  const numericKeys = simpleKeys.filter((key) => {
    const sampleValue = data[0]?.[key];
    return typeof sampleValue === "number";
  });

  // Identify other non-numeric columns that aren't name columns
  let otherNonNumericKeys = simpleKeys.filter((key) => {
    const sampleValue = data[0]?.[key];
    return typeof sampleValue !== "number" && !nameColumns.includes(key);
  });
  if (dataSources?.length === 1) {
    otherNonNumericKeys = otherNonNumericKeys.filter((key) => key !== "data_source");
  }
  if (regions?.length === 1) {
    otherNonNumericKeys = otherNonNumericKeys.filter((key) => key !== "region");
  }

  // Build the final column order: name columns first, then other non-numeric, then sort column, then other numeric
  let orderedKeys = [...nameColumns, ...otherNonNumericKeys];
  if (sortColumn && numericKeys.includes(sortColumn)) {
    orderedKeys.push(sortColumn);
    orderedKeys.push(...numericKeys.filter((key) => key !== sortColumn));
  } else {
    orderedKeys.push(...numericKeys);
  }

  return (
    <div>
      {(title || dataSources?.length) && (
        <div className="mb-3 flex items-center justify-between">
          {title && <h4 className="text-base font-medium">{title}</h4>}
          {dataSources?.length || regions?.length ? (
            <div className="flex flex-wrap gap-2">
              {dataSources?.map((dataSource) => (
                <Badge key={dataSource} variant="secondary" className="text-xs">
                  {getDataSourceDisplayName(dataSource)}
                </Badge>
              ))}
              {regions?.map((region) => (
                <Badge key={region} variant="outline" className="text-xs uppercase">
                  {region}
                </Badge>
              ))}
            </div>
          ) : null}
        </div>
      )}
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              {orderedKeys.map((key) => (
                <TableHead key={key} className="text-xs">
                  {formatColumnHeader(key)}
                  {key === sortColumn && " ↓"}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {sortedData.slice(0, 10).map((item, index) => (
              <TableRow key={index}>
                {orderedKeys.map((key) => {
                  let value = item[key];

                  // Process path columns to show only leaf node
                  if (key.includes("_path") && typeof value === "string") {
                    value = extractLeafFromPath(value);
                  }

                  // Determine the appropriate capitalization class
                  const capitalClass = key === "region" ? "uppercase" : "first-letter:capitalize";

                  // Determine color for numeric values (only for growth metrics)
                  let colorClass = "";
                  if (typeof value === "number" && isGrowthMetric(key)) {
                    if (value < 0) {
                      colorClass = "text-red-600 dark:text-red-400";
                    } else if (value > 0) {
                      colorClass = "text-green-600 dark:text-green-400";
                    }
                  }

                  return (
                    <TableCell
                      key={key}
                      className={`text-sm ${typeof value !== "number" ? capitalClass : ""} ${colorClass}`}
                    >
                      {typeof value === "number" ? formatNumber(value, key) : value || "-"}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      {data.length > 10 && (
        <p className="text-xs text-muted-foreground mt-2">Showing 10 of {data.length} items</p>
      )}
    </div>
  );
}

// Main component to intelligently render data
export function DataVisualization({ data, title }) {
  if (!data) return null;

  // Handle empty arrays
  if (Array.isArray(data) && data.length === 0) return null;

  // Check if data is an array with nested data objects containing time_series
  if (Array.isArray(data) && data.length > 0 && data[0].data && data[0].time_series) {
    // Use carousel for multiple time series
    return <TimeSeriesCarousel items={data} />;
  }

  // Check if it's time series data
  const isTimeSeries =
    Array.isArray(data) &&
    data.length > 0 &&
    data[0].date &&
    (data[0].real_volume !== undefined || data[0].volume !== undefined);

  // Check if it's an array of objects
  const isArrayData = Array.isArray(data);

  // Single data point
  if (!isArrayData || data.length === 1) {
    const item = isArrayData ? data[0] : data;

    // Handle empty objects
    if (!item || Object.keys(item).length === 0) return null;

    // Filter out UUID fields and complex objects
    const skipFields = ["item_id", "trend_id", "path"];
    const keys = Object.keys(item).filter((key) => {
      if (skipFields.includes(key)) return false;
      return typeof item[key] !== "object";
    });

    // Handle objects with no displayable properties
    if (keys.length === 0) return null;

    // For single item with path and metrics, show as a card with formatted info
    if (item.item_path || item.trend_path) {
      const pathToShow = formatPath(item.item_path || item.trend_path);
      const dataSource = item.data_source;

      return (
        <Card>
          <CardHeader>
            <div className="space-y-2">
              <CardTitle className="text-lg">{pathToShow}</CardTitle>
              <div className="flex items-center gap-2">
                {dataSource && (
                  <Badge variant="secondary" className="text-xs">
                    {getDataSourceDisplayName(dataSource)}
                  </Badge>
                )}
                {item.region && item.region !== "global" && (
                  <Badge variant="outline" className="text-xs uppercase">
                    {item.region}
                  </Badge>
                )}
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {item.yoy_percent_increase !== undefined && (
                <div className="space-y-1">
                  <p className="text-sm text-muted-foreground">YoY Change</p>
                  <p
                    className={`text-2xl font-bold ${item.yoy_percent_increase < 0 ? "text-red-600 dark:text-red-400" : item.yoy_percent_increase > 0 ? "text-green-600 dark:text-green-400" : ""}`}
                  >
                    {formatNumber(item.yoy_percent_increase, "yoy_percent_increase")}
                  </p>
                </div>
              )}
              {item.yoy_raw_increase !== undefined && (
                <div className="space-y-1">
                  <p className="text-sm text-muted-foreground">YoY Volume Increase</p>
                  <p
                    className={`text-2xl font-bold ${item.yoy_raw_increase < 0 ? "text-red-600 dark:text-red-400" : item.yoy_raw_increase > 0 ? "text-green-600 dark:text-green-400" : ""}`}
                  >
                    {formatNumber(item.yoy_raw_increase, "yoy_raw_increase")}
                  </p>
                </div>
              )}
              {keys
                .filter(
                  (k) =>
                    ![
                      "yoy_percent_increase",
                      "yoy_raw_increase",
                      "data_source",
                      "region",
                      "item_path",
                      "trend_path",
                    ].includes(k)
                )
                .map((key) => (
                  <div key={key} className="space-y-1">
                    <p className="text-sm text-muted-foreground">{formatColumnHeader(key)}</p>
                    <p className="text-2xl font-bold">
                      {typeof item[key] === "number" ? formatNumber(item[key], key) : item[key]}
                    </p>
                  </div>
                ))}
            </div>
          </CardContent>
        </Card>
      );
    }

    // Fallback for simple objects with few properties
    if (keys.length <= 3) {
      return (
        <div className="flex flex-wrap gap-4 mb-4">
          {keys.map((key) => {
            const label = key.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());
            return <MetricCard key={key} label={label} value={item[key]} />;
          })}
        </div>
      );
    }
  }

  // Time series data
  if (isTimeSeries) {
    // Try to extract data_source from the first item if it exists
    const dataSource = data[0]?.data_source || null;
    return <TimeSeriesChart data={data} title={title} dataSource={dataSource} />;
  }

  // Multiple data points - show as table
  if (isArrayData && data.length > 1) {
    const dataSources = [...new Set(data.map((item) => item.data_source).filter(Boolean))];
    const regions = [...new Set(data.map((item) => item.region).filter(Boolean))];
    return <DataTable data={data} title={title} dataSources={dataSources} regions={regions} />;
  }

  // Fallback to JSON
  return (
    <Card>
      <CardContent className="pt-6">
        <pre className="p-3 bg-muted rounded-lg font-mono text-xs leading-relaxed whitespace-pre-wrap break-words">
          {JSON.stringify(data, null, 2)}
        </pre>
      </CardContent>
    </Card>
  );
}

export default DataVisualization;
